import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Anchor, Button, Stack, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useMemberMeService } from '@actschurch/ccms-sdk';
import { useMemberMe, useInvalidateMe } from '@/lib/me';
import { PageIntro } from '@/components/rides/PageIntro';
import { Callout, DateOfBirthInput, ErrorState, LoadingState, PhoneInput, PickerCard, normaliseAnyPhone } from '@/components/ui';
import { useFocusMode } from '@/components/shell/focusMode';
import { errorMessage } from '@/lib/copy';
import { useCopy, supportLink } from '@/lib/settings';

type Sex = 'Male' | 'Female';
type Errors = Partial<Record<'dob' | 'sex' | 'cell', string>>;

/**
 * Carpool only needs three things from the member profile: date of birth (under-18 rules), sex
 * (safety matching) and a cellphone number (campus security on the day). Anything missing is
 * asked for on one short screen and saved with PUT members/me. The tab bar and footer step aside
 * while it shows, so the new member has one thing to do.
 */
export function RidesProfileGate({ children }: { children: ReactNode }) {
  const { data: member, isLoading, error, refetch, isRefetching } = useMemberMe();
  const complete = !!member && !!member.dateOfBirth && !!member.gender && !!member.cellNumber?.trim();
  const showForm = !isLoading && !error && !complete;
  useFocusMode(showForm);

  if (isLoading) {
    return <div className="rides-main"><LoadingState variant="spinner" label="Loading your profile" /></div>;
  }
  if (error) return <ProfileLoadError error={error} onRetry={() => refetch()} retrying={isRefetching} />;
  if (complete) return <>{children}</>;
  return <ProfileForm />;
}

function ProfileLoadError({ error, onRetry, retrying }: { error: unknown; onRetry: () => void; retrying: boolean }) {
  const { settings } = useCopy();
  const support = supportLink(settings);
  return (
    <div className="rides-main">
      <ErrorState
        title="We couldn't load your profile"
        message={errorMessage(error)}
        onRetry={onRetry}
        retrying={retrying}
        secondaryAction={support ? <Text ta="center" size="sm" c="dimmed">Still not working? <Anchor href={support.href}>Contact us</Anchor></Text> : undefined}
      />
    </div>
  );
}

function ProfileForm() {
  const { data: member, refetch } = useMemberMe();
  const members = useMemberMeService();
  const invalidate = useInvalidateMe();
  const { t } = useCopy();
  const formRef = useRef<HTMLDivElement>(null);

  const [dob, setDob] = useState<string | null>(null);
  const [sex, setSex] = useState<Sex | null>(null);
  const [cell, setCell] = useState('');
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (!member) return;
    if (member.dateOfBirth) setDob(String(member.dateOfBirth).slice(0, 10));
    if (member.gender) setSex(normaliseSex(member.gender));
    if (member.cellNumber) setCell(member.cellNumber);
  }, [member]);

  const save = useMutation({
    mutationFn: () =>
      members.updateMe({
        dateOfBirth: dob,
        gender: sex ?? undefined,
        cellNumber: cell.trim(),
      }),
    onSuccess: () => { invalidate(); refetch(); },
  });

  const submit = () => {
    const e: Errors = {};
    if (!dob) e.dob = 'Please choose your day, month and year of birth.';
    else if (dob > new Date().toISOString().slice(0, 10)) e.dob = 'That date is in the future. Please check it.';
    if (!sex) e.sex = 'Please choose one.';
    if (!normaliseAnyPhone(cell)) e.cell = 'Please enter your cellphone number, for example 082 123 4567.';
    setErrors(e);
    if (Object.keys(e).length) {
      window.requestAnimationFrame(() => {
        const first = formRef.current?.querySelector<HTMLElement>('[data-error="true"]');
        first?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        first?.querySelector<HTMLElement>('select, input, button')?.focus();
      });
      return;
    }
    save.mutate();
  };

  return (
    <div className="rides-main">
      <PageIntro title="Tell us a little about you" lede={t('profile_gate.intro')} />
      <div className="rides-card" style={{ padding: 18 }} ref={formRef}>
        <Stack gap="lg">
          {save.error && <Callout tone="bad" live>{errorMessage(save.error)}</Callout>}
          <div data-error={errors.dob ? 'true' : undefined}>
            <DateOfBirthInput
              value={dob}
              onChange={(v) => { setDob(v); setErrors((x) => ({ ...x, dob: undefined })); }}
              required
              description={t('profile_gate.dob_help')}
              error={errors.dob}
            />
          </div>
          <div data-error={errors.sex ? 'true' : undefined}>
            <div id="gate-sex-label" style={{ fontWeight: 600, fontSize: 16 }}>Sex<span className="rides-req" aria-hidden="true">*</span></div>
            <p id="gate-sex-help" style={{ margin: '2px 0 8px', color: 'var(--rides-muted)', fontSize: 14 }}>{t('profile_gate.sex_help')}</p>
            <div className="rides-seg" role="radiogroup" aria-labelledby="gate-sex-label" aria-describedby="gate-sex-help">
              {(['Female', 'Male'] as Sex[]).map((v) => (
                <PickerCard
                  key={v}
                  selected={sex === v}
                  onSelect={() => { setSex(v); setErrors((x) => ({ ...x, sex: undefined })); }}
                  title={v}
                />
              ))}
            </div>
            {errors.sex && <Text c="red" size="sm" fw={500} mt={6} role="alert">{errors.sex}</Text>}
          </div>
          <div data-error={errors.cell ? 'true' : undefined}>
            <PhoneInput
              required
              value={cell}
              onChange={(v) => { setCell(v); setErrors((x) => ({ ...x, cell: undefined })); }}
              description={t('profile_gate.phone_help')}
              error={errors.cell}
            />
          </div>
          <Button fullWidth loading={save.isPending} onClick={submit} rightSection={<IconArrowRight size={20} />}>
            Save and continue
          </Button>
        </Stack>
      </div>
    </div>
  );
}

function normaliseSex(g: string): Sex | null {
  const v = g.trim().toLowerCase();
  if (v === 'male' || v === 'm') return 'Male';
  if (v === 'female' || v === 'f') return 'Female';
  return null;
}
